const SESSION_STORAGE_KEY = "user_token";
export const ACCOUNT_ACCESS_ERROR_EVENT = "mvp:account-access-error";

export function reportAccountAccessError(error) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }

  const message = error?.message || "Accounts could not be loaded. Please try again.";
  window.dispatchEvent(new CustomEvent(ACCOUNT_ACCESS_ERROR_EVENT, {
    detail: { message },
  }));
}

export function readSessionUser(storage = window.sessionStorage) {
  const rawUser = storage.getItem(SESSION_STORAGE_KEY);

  if (!rawUser) {
    return {
      user: null,
      error: "Your login session is missing. Please sign in again.",
    };
  }

  try {
    const user = JSON.parse(rawUser);

    if (!user || typeof user !== "object" || Array.isArray(user)) {
      throw new Error("Invalid session value");
    }

    return { user, error: "" };
  } catch (error) {
    return {
      user: null,
      error: "Your login session is invalid. Please sign out and sign in again.",
    };
  }
}

export function getUserAccountType(user) {
  if (typeof user?.account_type === "string") {
    return user.account_type.trim();
  }

  if (typeof user?.account_type?.account_type === "string") {
    return user.account_type.account_type.trim();
  }

  return "";
}

function normalizeText(value) {
  return value === undefined || value === null ? "" : String(value).trim();
}

/**
 * Account locking follows the effective access resolved by the backend, not a
 * hard-coded role name. Unrestricted administrators remain selectable even
 * when a test database happens to contain only one active account.
 */
export function isAccountScopedPortalUser(user) {
  if (!user || typeof user !== "object") {
    return false;
  }

  const accountScope = normalizeText(
    user.role_capabilities?.account_scope
  ).toLowerCase();

  if (accountScope === "all accounts") {
    return false;
  }

  // all_modules_access controls which modules are visible; it must never grant
  // additional vendor accounts. New login snapshots always include account_scope.
  if (accountScope) {
    return true;
  }

  // Preserve compatibility for older administrator sessions that pre-date the
  // account_scope field, while scoped legacy users remain locked down.
  return user.role_capabilities?.all_modules_access !== true;
}

export function getAccessibleAccountIdentity(account) {
  if (!account || typeof account !== "object" || Array.isArray(account)) {
    return { id: "", name: "" };
  }

  return {
    id: normalizeText(
      account._id
      ?? account.company_id
      ?? account.account_id
      ?? account.companyNumber
      ?? account.company_number
    ),
    name: normalizeText(
      account.company_name
      ?? account.account_name
      ?? account.companyName
      ?? account.name
    ),
  };
}

export function findAccessibleAccountById(accounts, accountId) {
  const normalizedAccountId = normalizeText(accountId);
  if (!normalizedAccountId || !Array.isArray(accounts)) return null;

  return accounts.find((account) => (
    getAccessibleAccountIdentity(account).id === normalizedAccountId
  )) || null;
}

export function buildAccessibleAccountOptions(accounts) {
  return (Array.isArray(accounts) ? accounts : []).reduce((options, account) => {
    const { id, name } = getAccessibleAccountIdentity(account);
    if (id && name) {
      options.push({ value: id, label: name });
    }
    return options;
  }, []);
}

/**
 * Produces the common state needed by account selectors without causing any
 * React side effects. An account-scoped user is auto-selected and locked only
 * when the backend-resolved accessible set contains one distinct account.
 * Multi-account users and unrestricted administrators remain selectable.
 */
export function deriveVendorAccountSelection(accounts, user, currentAccountId = "") {
  const distinctAccounts = [];
  const seenAccountIds = new Set();

  if (Array.isArray(accounts)) {
    accounts.forEach((account) => {
      const { id } = getAccessibleAccountIdentity(account);

      if (!id || seenAccountIds.has(id)) {
        return;
      }

      seenAccountIds.add(id);
      distinctAccounts.push(account);
    });
  }

  const normalizedCurrentAccountId = normalizeText(currentAccountId);
  const currentAccount = normalizedCurrentAccountId
    ? findAccessibleAccountById(distinctAccounts, normalizedCurrentAccountId)
    : null;
  const isSingleAccountUser = isAccountScopedPortalUser(user) && distinctAccounts.length === 1;
  const selectedAccount = currentAccount || (isSingleAccountUser ? distinctAccounts[0] : null);
  const selectedIdentity = getAccessibleAccountIdentity(selectedAccount);

  return {
    accounts: distinctAccounts,
    selectedAccount,
    selectedAccountId: selectedIdentity.id,
    selectedAccountName: selectedIdentity.name,
    shouldAutoSelect: isSingleAccountUser,
    isSelectorDisabled: isSingleAccountUser,
  };
}

export function buildAccessWiseCompanyUrl(
  baseURL,
  accountAccess,
  accountId,
  accountType
) {
  const values = [accountAccess, accountId, accountType];

  if (values.some((value) => value === undefined || value === null || String(value).trim() === "")) {
    throw new Error("Your account access details are incomplete. Please sign out and sign in again.");
  }

  const encodedPath = values
    .map((value) => encodeURIComponent(String(value).trim()))
    .join("*");

  return `${baseURL}/get_company_details_for_users_accessWise/${encodedPath}`;
}

export async function fetchJson(url, options = {}, label = "Request") {
  let response;

  try {
    response = await fetch(url, options);
  } catch (error) {
    throw new Error(`${label} could not reach the server. Please try again.`);
  }

  if (!response.ok) {
    throw new Error(`${label} failed (${response.status}). Please try again.`);
  }

  try {
    return await response.json();
  } catch (error) {
    throw new Error(`${label} returned an invalid response.`);
  }
}

export function requireArray(value, label) {
  if (!Array.isArray(value)) {
    throw new Error(`${label} returned an unexpected response.`);
  }

  return value;
}

export async function loadAccessibleCompanies(
  baseURL,
  user,
  permissionKey,
  options = {}
) {
  const { suppressGlobalError = false, ...requestOptions } = options;

  try {
    if (!user || typeof user !== "object") {
      throw new Error("Your login session is unavailable. Please sign in again.");
    }
    if (!user.account_id || !user.userEmail || !permissionKey) {
      throw new Error("Your account access details are incomplete. Please sign out and sign in again.");
    }

    const headers = {
      "Content-Type": "application/json",
      ...(requestOptions.headers || {}),
    };
    const data = await fetchJson(
      `${baseURL}/get_accessible_companies`,
      {
        ...requestOptions,
        method: "POST",
        headers,
        body: JSON.stringify({
          companyNumber: String(user.account_id || ""),
          user_email: user.userEmail,
          permission_key: permissionKey,
        }),
      },
      "Account list"
    );
    return requireArray(data, "Account list");
  } catch (error) {
    if (!suppressGlobalError) reportAccountAccessError(error);
    throw error;
  }
}
